import { CLEAR_ERRORS, SET_ERRORS } from "../../constants/types";
import { Nullable } from "../../types/common";
import { LoadingState, LOADING_STATES } from "../../features/loading";
import { IUser } from "../../types/user/iuser.interface";
import { ApprovalStatus } from "../../types/approval-status/approval-status.type";
import { IBreadcrumbInfo } from "../../features/brief-header/types/ibreadcrumb-info.interface";
import { ExecutionBriefActions } from "../../pages/execution-brief/store/execution-brief.actions";

import {
  CLEAR_EXECUTION_BRIEF_ERRORS,
  EXECUTION_BRIEF_PAGE_CHANGE,
  FAILURE_NEW_EXECUTION_PAGE,
  FAILURE_UPDATE_EXECUTION_PAGE,
  FETCH_EXECUTION_BRIEF_FILTERS_SUCCESS,
  LIST_EXECUTION_BRIEFS,
  LOADING_EXECUTION_BRIEFS,
  RESET_EXECUTION_BRIEF,
  RESET_EXECUTION_BRIEF_ATTRIBUTE,
  SET_EXECUTION_BRIEFS_FOR_CAMPAIGN,
  SET_EXECUTION_BRIEF_DATA,
  SET_EXECUTION_BRIEF_ERRORS,
  SET_EXECUTION_BRIEF_PARENT_STATUS,
  SET_EXECUTION_BRIEF_STATUS,
  SUCCESS_NEW_EXECUTION_PAGE,
  SUCCESS_UPDATE_EXECUTION_PAGE,
  SET_EXECUTION_BRIEF_LOADING,
  SUCCESS_RENAME_EXECUTION_PAGE,
} from "../actions/executionBriefListActions";
import { IError } from "../actions/errorHandlingAction";
import { executionBriefNormaliseFormData } from "../utils/execution-brief.normalise";
import { ExecutionBriefWorkOrdersState } from "../../pages/execution-brief/types/execution-brief-work-orders/execution-brief-work-orders.interface";
import { createExecutionBriefWorkOrdersState } from "../../pages/execution-brief/types/execution-brief-work-orders/execution-brief-work-orders.functions";
import { executionBriefWorkOrdersReducer } from "../../pages/execution-brief/store/execution-brief-work-orders/execution-brief-work-orders.reducer";
import { ExecutionBriefWorkOrdersActionTypes } from "../../pages/execution-brief/store/execution-brief-work-orders/execution-brief-work-orders.actions";

export interface IExecutionBriefsState {
  executionBriefs: ExecutionBriefsState;
}

interface IExecutionBriefHierarchyDetails {
  campaignBriefId: Nullable<number>;
  campaignBriefOwnerId: Nullable<number>;
  campaignBriefReviewers: number[];
  workOrderContentCreators: number[];
}

export interface IExecutionBriefBasicInfo {
  id: number;
  name: string;
  date?: string;
  startAt: string;
  endAt: string;
  region?: string;
  reviewers?: any[];
  status: ApprovalStatus;
  regions: number[];
}
export interface TExecutionBriefResults extends IExecutionBriefBasicInfo {
  areVendorAssetsProvided: Nullable<boolean>;
  audience?: string;
  background?: string;
  breadcrumbInfo?: IBreadcrumbInfo;
  briefOwner: {
    id: number;
    firstName: string;
    lastName: string;
  };
  businessObjectives?: string;
  campaignBrief: number;
  campaignBriefId: number;
  copyDirection: string;
  createdAt: string;
  creativeDirection: string;
  endAt: string;
  executionDetails?: string;
  executionName?: string;
  executionTemplate?: string;
  flyerPeriod?: string;
  hasVendorCommitment: Nullable<boolean>;
  hierarchyDetails: IExecutionBriefHierarchyDetails;
  isInventoryAvailable?: Nullable<boolean>;
  isBuyGet: Nullable<boolean>;
  isRegional: Nullable<boolean>;
  isUnderNda: Nullable<boolean>;
  keyMessage?: string;
  languages: number[];
  marketingChannels: number[];
  merchOwnerDetails: IUser[];
  merchOwners: number[];
  numberOfWorkOrdersRequested: number | string;
  parentStatus: ApprovalStatus;
  polymorphicCtype: number;
  programObjectives?: string;
  projectOverview?: string;
  reportingMasterCampaignName: string;
  resourcetype: string;
  startAt: string;
  storeNumber: string;
  storeType: Nullable<number | string>;
  underNdaUserDetails: IUser[];
  updatedAt: string;
  vendorAssetsLocation: string;
}

interface IExecutionBriefsFilters {
  briefOwners: IUser[];
}

export interface ExecutionBriefsState {
  count: number;
  currentTab?: number;
  data: TExecutionBriefResults;
  errors: Nullable<IError>;
  filters: IExecutionBriefsFilters;
  formErrors: Nullable<IError>;
  loading: LoadingState;
  next: Nullable<number>;
  parentStatus: ApprovalStatus;
  previous: Nullable<number>;
  results: TExecutionBriefResults[];
  workOrders: ExecutionBriefWorkOrdersState;
}

const initialState: ExecutionBriefsState = {
  loading: { ...LOADING_STATES.default },
  count: 0,
  filters: { briefOwners: [] },
  next: null,
  previous: null,
  errors: null,
  formErrors: null,
  data: {
    areVendorAssetsProvided: false,
    breadcrumbInfo: { parentName: "" },
    briefOwner: {
      id: 0,
      firstName: "",
      lastName: "",
    },
    campaignBrief: 0,
    campaignBriefId: 0,
    copyDirection: "",
    creativeDirection: "",
    createdAt: "",
    date: "",
    endAt: "",
    hasVendorCommitment: false,
    hierarchyDetails: {
      campaignBriefId: 0,
      campaignBriefOwnerId: 0,
      campaignBriefReviewers: [],
      workOrderContentCreators: [],
    },
    id: 0,
    isBuyGet: false,
    isRegional: false,
    isUnderNda: false,
    languages: [],
    marketingChannels: [],
    merchOwners: [],
    merchOwnerDetails: [],
    name: "",
    numberOfWorkOrdersRequested: 0,
    parentStatus: "draft",
    polymorphicCtype: 0,
    projectOverview: "",
    regions: [],
    reportingMasterCampaignName: "",
    resourcetype: "",
    reviewers: [],
    startAt: "",
    status: "draft",
    storeNumber: "",
    storeType: null,
    underNdaUserDetails: [],
    updatedAt: "",
    vendorAssetsLocation: "",
  },
  results: [
    {
      areVendorAssetsProvided: false,
      breadcrumbInfo: { parentName: "" },
      briefOwner: {
        id: 0,
        firstName: "",
        lastName: "",
      },
      campaignBrief: 0,
      campaignBriefId: 0,
      copyDirection: "",
      creativeDirection: "",
      createdAt: "",
      date: "",
      endAt: "",
      hasVendorCommitment: false,
      hierarchyDetails: {
        campaignBriefId: 0,
        campaignBriefOwnerId: 0,
        campaignBriefReviewers: [],
        workOrderContentCreators: [],
      },
      id: 0,
      isBuyGet: false,
      isRegional: false,
      isUnderNda: false,
      languages: [],
      marketingChannels: [],
      merchOwners: [],
      merchOwnerDetails: [],
      name: "",
      numberOfWorkOrdersRequested: 0,
      parentStatus: "draft",
      polymorphicCtype: 0,
      projectOverview: "",
      regions: [],
      reportingMasterCampaignName: "",
      resourcetype: "",
      reviewers: [],
      startAt: "",
      status: "draft",
      storeNumber: "",
      storeType: 0,
      underNdaUserDetails: [],
      updatedAt: "",
      vendorAssetsLocation: "",
    },
  ],
  parentStatus: "draft",
  workOrders: createExecutionBriefWorkOrdersState(),
};

const executionBriefsReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case SET_EXECUTION_BRIEF_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case FETCH_EXECUTION_BRIEF_FILTERS_SUCCESS:
      return {
        ...state,
        filters: action.payload,
      };
    case SET_ERRORS:
      return {
        ...state,
        loading: { ...LOADING_STATES.failed },
        errors: action.payload,
        ...action.payload,
      };
    case CLEAR_ERRORS:
      return {
        ...state,
        errors: null,
        ...action.payload,
      };
    case EXECUTION_BRIEF_PAGE_CHANGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loading },
        errors: null,
      };
    case LOADING_EXECUTION_BRIEFS:
      return {
        ...state,
        loading: { ...LOADING_STATES.loading },
        errors: null,
        count: 0,
        results: [],
        prev: null,
        next: null,
      };
    case LIST_EXECUTION_BRIEFS:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: null,
        ...action.payload,
      };
    case SET_EXECUTION_BRIEFS_FOR_CAMPAIGN:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: null,
        results: action.payload.executionBriefs,
      };
    case ExecutionBriefActions.DuplicateSuccess:
    case SUCCESS_NEW_EXECUTION_PAGE:
      return {
        ...initialState,
        loading: { ...LOADING_STATES.loaded },
        data: executionBriefNormaliseFormData(action.payload),
        errors: null,
      };
    case ExecutionBriefActions.DuplicateFailure:
    case FAILURE_NEW_EXECUTION_PAGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: action.payload,
      };
    case SUCCESS_UPDATE_EXECUTION_PAGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: executionBriefNormaliseFormData(action.payload),
      };
    case SUCCESS_RENAME_EXECUTION_PAGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: {
          ...state.data,
          name: action.payload.name,
        },
      };
    case FAILURE_UPDATE_EXECUTION_PAGE:
      const errorType = action.payload?.status === 400 ? "formErrors" : "errors";
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        [errorType]: action.payload,
      };
    case SET_EXECUTION_BRIEF_DATA:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: executionBriefNormaliseFormData(action.payload.data),
      };
    case SET_EXECUTION_BRIEF_ERRORS:
      return {
        ...state,
        loading: { ...LOADING_STATES.failed },
        errors: action.payload,
      };
    case CLEAR_EXECUTION_BRIEF_ERRORS:
      return {
        ...state,
        errors: null,
      };
    case RESET_EXECUTION_BRIEF_ATTRIBUTE:
      const { attributeName } = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          [attributeName]: "",
        },
      };
    case SET_EXECUTION_BRIEF_STATUS:
      return {
        ...state,
        data: {
          ...state.data,
          status: action.payload.status,
        },
      };
    case SET_EXECUTION_BRIEF_PARENT_STATUS:
      return {
        ...state,
        parentStatus: action.payload.parentStatus,
      };
    case RESET_EXECUTION_BRIEF:
      return {
        ...initialState,
      };
    default:
      return {
        ...state,
        workOrders: executionBriefWorkOrdersReducer(
          state.workOrders,
          action as ExecutionBriefWorkOrdersActionTypes
        ),
      };
  }
};

export default executionBriefsReducer;
